"use client";

import Image from "next/image";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";

import { ApiRequestError } from "@/lib/auth-api";
import {
    analyzeBrowserSession,
    closeBrowserSession,
    createBrowserSession,
    executeBrowserAction,
    prepareBrowserAction,
    type BrowserAction,
    type BrowserActionPlan,
    type BrowserElement,
    type BrowserSession,
} from "@/lib/browser-api";
import type { PageTestSource } from "@/lib/qa-types";

export function BrowserLabView({
    csrfToken,
    onCreateScenarios,
    restoredSource,
}: {
    csrfToken: string | null;
    onCreateScenarios?: (source: PageTestSource) => void;
    restoredSource?: PageTestSource | null;
}) {
    const [targetUrl, setTargetUrl] = useState(restoredSource?.target_url ?? "http://localhost:3000/");
    const [requirement, setRequirement] = useState(restoredSource?.requirement ?? "");
    const [session, setSession] = useState<BrowserSession | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [action, setAction] = useState<BrowserAction>("click");
    const [value, setValue] = useState("");
    const [plan, setPlan] = useState<BrowserActionPlan | null>(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);
    const sessionRef = useRef<BrowserSession | null>(null);

    const restoredSession = useMemo<BrowserSession | null>(() => {
        const exploration = restoredSource?.exploration;
        if (!restoredSource || !exploration) return null;
        return {
            session_id: exploration.session_id,
            status: "CLOSED",
            target_url: restoredSource.target_url,
            requirement: restoredSource.requirement,
            current_url: exploration.current_url,
            title: restoredSource.title,
            revision: exploration.revision,
            screenshot_data_url: exploration.screenshot_data_url,
            elements: exploration.elements,
            events: exploration.events,
        };
    }, [restoredSource]);
    const visibleSession = session ?? restoredSession;

    const selected = useMemo(() => session?.elements.find((element) => element.element_id === selectedId) ?? null, [selectedId, session]);
    const sensitiveTarget = selected?.input_type === "file";

    useEffect(() => {
        return () => {
            const current = sessionRef.current;
            if (current && csrfToken) void closeBrowserSession(current.session_id, csrfToken).catch(() => undefined);
        };
    }, [csrfToken]);

    async function start(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        const token = requireToken();
        if (!token) return;
        setBusy(true);
        setMessage(null);
        try {
            if (session) {
                await closeBrowserSession(session.session_id, token);
                setSession(null);
                sessionRef.current = null;
                setSelectedId(null);
                setPlan(null);
            }
            const created = await createBrowserSession(targetUrl.trim(), requirement, token);
            applySession(created);
            setMessage({ kind: "success", text: `已识别 ${created.elements.length} 个可交互元素` });
            onCreateScenarios?.(pageTestSource(created));
        } catch (error) {
            setMessage({ kind: "error", text: browserErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    }

    async function analyze() {
        const token = requireToken();
        if (!token || !session) return;
        setBusy(true);
        setMessage(null);
        try {
            const analyzed = await analyzeBrowserSession(session.session_id, token);
            applySession(analyzed);
            setMessage({ kind: "success", text: `页面已刷新分析，共 ${analyzed.elements.length} 个元素` });
        } catch (error) {
            setMessage({ kind: "error", text: browserErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    }

    async function prepare() {
        const token = requireToken();
        if (!token || !session || !selected) return;
        setBusy(true);
        setMessage(null);
        try {
            const prepared = await prepareBrowserAction(session, selected.element_id, action, value, token);
            setPlan(prepared);
            setValue("");
            setSession({ ...session, status: "AWAITING_CONFIRMATION" });
        } catch (error) {
            setMessage({ kind: "error", text: browserErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    }

    async function execute() {
        const token = requireToken();
        if (!token || !session || !plan) return;
        setBusy(true);
        setMessage(null);
        try {
            const executed = await executeBrowserAction(session.session_id, plan.plan_id, token);
            applySession(executed);
            setValue("");
            setMessage({ kind: "success", text: "动作已执行，页面证据已更新" });
        } catch (error) {
            setPlan(null);
            setSession({ ...session, status: "ACTIVE" });
            setMessage({ kind: "error", text: browserErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    }

    async function close() {
        const token = requireToken();
        if (!token || !session) return;
        setBusy(true);
        try {
            await closeBrowserSession(session.session_id, token);
            setSession(null);
            sessionRef.current = null;
            setSelectedId(null);
            setPlan(null);
            setMessage({ kind: "success", text: "浏览器会话已关闭" });
        } catch (error) {
            setMessage({ kind: "error", text: browserErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    }

    function applySession(next: BrowserSession) {
        setSession(next);
        sessionRef.current = next;
        setPlan(null);
        setSelectedId(next.elements[0]?.element_id ?? null);
        setAction(defaultAction(next.elements[0]));
    }

    function chooseElement(element: BrowserElement) {
        setSelectedId(element.element_id);
        setAction(defaultAction(element));
        setValue("");
        setPlan(null);
    }

    function requireToken(): string | null {
        if (csrfToken) return csrfToken;
        setMessage({ kind: "error", text: "当前会话缺少写入令牌，请退出后重新登录" });
        return null;
    }

    return (
        <section className="workspace-view browser-lab-view" aria-labelledby="browser-lab-title">
            <div className="view-heading">
                <div>
                    <p className="section-kicker">BROWSER EXPLORATION · MVP</p>
                    <h1 id="browser-lab-title">质量探索与Agent自动化</h1>
                    <p>分析公网或本地开发页面的可交互元素；所有点击和输入均需人工二次确认。</p>
                </div>
                <span className={`browser-session-status ${visibleSession ? "active" : "idle"}`}>
                    {session ? `● ${session.status}` : restoredSession ? "✓ 已恢复快照" : "○ 未启动"}
                </span>
            </div>

            <form className="card browser-target-form" onSubmit={start}>
                <label>
                    <span>被测页面 URL</span>
                    <input
                        disabled={busy}
                        onChange={(event) => setTargetUrl(event.target.value)}
                        placeholder="http://localhost:3000/"
                        required
                        type="url"
                        value={targetUrl}
                    />
                </label>
                <label className="browser-requirement-field">
                    <span>测试需求（可选）</span>
                    <textarea
                        aria-label="测试需求（可选）"
                        disabled={busy}
                        maxLength={2000}
                        onChange={(event) => setRequirement(event.target.value)}
                        placeholder="例如：验证管理员登录；检查错误密码提示；登录后进入订单中心"
                        rows={3}
                        value={requirement}
                    />
                    <small>{requirement.length}/2000 · 启动后将自动生成场景、执行任务并生成质量总览</small>
                </label>
                <button className="button primary" disabled={busy} type="submit">
                    {busy && !session ? "正在启动…" : "启动并分析"}
                </button>
                {session && (
                    <>
                        <button className="button secondary" disabled={busy} onClick={() => void analyze()} type="button">
                            重新分析
                        </button>
                        <button className="button secondary danger" disabled={busy} onClick={() => void close()} type="button">
                            关闭会话
                        </button>
                    </>
                )}
            </form>

            {message && (
                <div className={`form-message browser-message ${message.kind}`} role={message.kind === "error" ? "alert" : "status"}>
                    {message.text}
                </div>
            )}

            {visibleSession ? (
                <div className="browser-lab-grid">
                    <div className="card browser-preview-card">
                        <div className="browser-preview-head">
                            <div>
                                <strong>{visibleSession.title}</strong>
                                <span>{visibleSession.current_url}</span>
                            </div>
                            <small>快照 #{visibleSession.revision}</small>
                        </div>
                        <div className="browser-screenshot">
                            <Image
                                alt={`${visibleSession.title} 的带编号页面分析截图`}
                                height={720}
                                priority
                                src={visibleSession.screenshot_data_url}
                                unoptimized
                                width={1280}
                            />
                        </div>
                        <div className="browser-evidence-note">
                            {session
                                ? "蓝色编号与右侧元素清单对应；阶段成果会随质量分析任务保存。"
                                : "这是已保存的探索快照；重新启动分析后才能继续执行页面操作。"}
                        </div>
                        {visibleSession.requirement && (
                            <div className="browser-requirement-context">
                                <strong>本次测试需求</strong>
                                <p>{visibleSession.requirement}</p>
                            </div>
                        )}
                    </div>

                    <aside className="card browser-control-card">
                        <div className="browser-control-head">
                            <div>
                                <p className="section-kicker">INTERACTIVE ELEMENTS</p>
                                <h2>{visibleSession.elements.length} 个可交互元素</h2>
                            </div>
                        </div>
                        <div className="browser-element-list">
                            {visibleSession.elements.map((element) => (
                                <button
                                    className={selectedId === element.element_id ? "selected" : ""}
                                    disabled={!session || element.disabled}
                                    key={element.element_id}
                                    onClick={() => session && chooseElement(element)}
                                    type="button"
                                >
                                    <b>{element.index}</b>
                                    <span>
                                        <strong>{element.name}</strong>
                                        <small>
                                            {element.role} · {element.tag}
                                            {element.input_type ? ` · ${element.input_type}` : ""}
                                        </small>
                                    </span>
                                </button>
                            ))}
                        </div>

                        {session && selected && (
                            <div className="browser-action-builder">
                                <strong>
                                    拟操作：#{selected.index} {selected.name}
                                </strong>
                                {sensitiveTarget ? (
                                    <div className="browser-sensitive-warning">文件上传仍禁止操作。</div>
                                ) : (
                                    <>
                                        <label>
                                            <span>动作</span>
                                            <select value={action} onChange={(event) => setAction(event.target.value as BrowserAction)}>
                                                <option value="click">点击</option>
                                                {selected.tag === "input" || selected.tag === "textarea" ? (
                                                    <option value="fill">填写</option>
                                                ) : null}
                                                {selected.tag === "select" ? <option value="select">选择</option> : null}
                                            </select>
                                        </label>
                                        {action !== "click" && (
                                            <label>
                                                <span>
                                                    {action === "select"
                                                        ? "选项值"
                                                        : selected.input_type === "password"
                                                          ? "密码"
                                                          : "测试输入"}
                                                </span>
                                                <input
                                                    autoComplete="off"
                                                    maxLength={500}
                                                    onChange={(event) => setValue(event.target.value)}
                                                    type={selected.input_type === "password" ? "password" : "text"}
                                                    value={value}
                                                />
                                            </label>
                                        )}
                                        <button
                                            className="button primary wide"
                                            disabled={busy}
                                            onClick={() => void prepare()}
                                            type="button"
                                        >
                                            生成待确认操作
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </aside>
                </div>
            ) : (
                <div className="card browser-empty-state">
                    <span>◎</span>
                    <h2>等待启动浏览器会话</h2>
                    <p>支持公网 HTTP/HTTPS 页面；本机、私网和云元数据地址仍受保护。</p>
                </div>
            )}

            {plan && session && (
                <div className="browser-confirm-backdrop" role="presentation">
                    <div aria-label="操作确认" aria-modal="true" className="card browser-confirm-dialog" role="dialog">
                        <p className="section-kicker">HUMAN CONFIRMATION</p>
                        <h2>确认执行模拟操作？</h2>
                        <dl>
                            <div>
                                <dt>目标</dt>
                                <dd>
                                    #{plan.target.index} {plan.target.name}
                                </dd>
                            </div>
                            <div>
                                <dt>动作</dt>
                                <dd>{actionLabel(plan.action)}</dd>
                            </div>
                            {plan.value_summary && (
                                <div>
                                    <dt>输入</dt>
                                    <dd>{plan.value_summary}，内容不写入日志</dd>
                                </div>
                            )}
                        </dl>
                        <p>执行前会再次校验页面版本和目标元素；页面已变化时操作会被拒绝。</p>
                        <div className="browser-confirm-actions">
                            <button
                                className="button secondary"
                                disabled={busy}
                                onClick={() => {
                                    setPlan(null);
                                    setValue("");
                                    setSession({ ...session, status: "ACTIVE" });
                                }}
                                type="button"
                            >
                                取消
                            </button>
                            <button className="button primary" disabled={busy} onClick={() => void execute()} type="button">
                                {busy ? "执行中…" : "确认执行"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {visibleSession && visibleSession.events.length > 0 && (
                <div className="card browser-event-card">
                    <p className="section-kicker">SESSION EVENTS</p>
                    <h2>最近步骤</h2>
                    {visibleSession.events.slice(-6).map((event, index) => (
                        <div className="browser-event" key={`${event.at}-${index}`}>
                            <span>{event.kind}</span>
                            <p>{event.detail}</p>
                            <time>{new Date(event.at).toLocaleTimeString("zh-CN", { hour12: false })}</time>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

function defaultAction(element: BrowserElement | undefined): BrowserAction {
    if (element?.tag === "select") return "select";
    if (element?.tag === "input" || element?.tag === "textarea") return "fill";
    return "click";
}

function pageTestSource(session: BrowserSession): PageTestSource {
    const counts = session.elements.reduce<Record<string, number>>((result, element) => {
        const kind = element.input_type ? `${element.input_type} 输入框` : element.role;
        result[kind] = (result[kind] ?? 0) + 1;
        return result;
    }, {});
    const characteristics = [
        `页面标题：${session.title}`,
        `识别到 ${session.elements.length} 个可交互元素`,
        ...Object.entries(counts).map(([kind, count]) => `${kind} ${count} 个`),
        ...session.elements.slice(0, 6).map((element) => `核心交互：${element.name}`),
    ];
    return {
        target_url: session.current_url,
        title: session.title,
        requirement: session.requirement || `分析 ${session.title} 的页面特性并验证主要业务流程、异常输入和边界行为`,
        characteristics,
        exploration: {
            session_id: session.session_id,
            current_url: session.current_url,
            revision: session.revision,
            screenshot_data_url: session.screenshot_data_url,
            elements: session.elements,
            events: session.events,
        },
    };
}

function actionLabel(action: BrowserAction): string {
    return { click: "点击", fill: "填写", select: "选择" }[action];
}

function browserErrorMessage(error: unknown): string {
    if (!(error instanceof ApiRequestError)) return "浏览器操作失败，请稍后重试";
    if (error.errorCode === "BROWSER_TARGET_BLOCKED") return "该地址指向本机、私网或受保护网络，不能访问";
    if (error.errorCode === "BROWSER_START_FAILED") return "浏览器运行环境启动失败，请检查后端 Node.js 和 Chromium 配置";
    if (error.errorCode === "BROWSER_CAPACITY_EXHAUSTED") return "浏览器会话已达上限，请先关闭其他会话";
    if (error.errorCode === "BROWSER_PAGE_CHANGED") return "页面已发生变化，请重新分析后再操作";
    if (error.errorCode === "SENSITIVE_INPUT_BLOCKED") return "文件上传仍禁止操作";
    if (error.errorCode === "BROWSER_ACTION_FAILED") return "动作执行失败，请重新分析页面";
    if (error.errorCode === "CSRF_INVALID") return "会话写入令牌已失效，请重新登录";
    return error.message;
}
